import React, { FC, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { setProfile, setToken } from "auth";
import { MagicLinkSession } from "auth/utils/magicLink";
import { UserProfile } from "generated/graphql";
import { State } from "redux/state";

import { FetchProfileComponent } from "components/Authorize/components/FetchProfileComponent";
import { MagicLinkSignIn } from "components/Login/components/MagicLinkSignIn";

/**
 * Route host for passwordless sign-in.
 *
 * Its own route rather than a button on `/sign-up`, for two reasons. `/login`
 * is not a form at all — it bounces straight to the Cognito hosted UI, which
 * cannot host a custom auth flow — and `/sign-up` renders `LoginForm` from
 * `@xapp/components`, which has no slot for another action.
 *
 * Once tokens come back this follows exactly the path the password flow takes:
 * `setToken`, then `FetchProfileComponent` to load the profile, then out to the
 * app. Nothing about being signed in should depend on which flow got you here.
 */
export const MagicLinkSignInContainer: FC = () => {
    const dispatch = useDispatch();

    /**
     * What the link in the sign-in email carries: `?e=<email>&code=<code>`
     * (stentor-api#6084).
     *
     * Read once, on the first render, and the code is taken straight back out
     * of the address bar. It is single-use and short-lived, but it has no
     * business sitting in browser history, in a screenshot, or in the referrer
     * of whatever the page loads next. The address stays -- it is not a secret,
     * and leaving it means a failed attempt lands on a pre-filled form.
     *
     * Read off `window.location` rather than the router: this container has
     * never needed a Router above it, and taking one now would be a new
     * requirement for every caller and every test.
     */
    const linkRef = useRef<{ email?: string; code?: string }>();
    if (!linkRef.current) {
        const params = new URLSearchParams(
            typeof window !== "undefined" ? window.location.search : ""
        );
        linkRef.current = {
            email: params.get("e") || undefined,
            code: params.get("code") || undefined
        };

        if (linkRef.current.code && typeof window !== "undefined" && window.history?.replaceState) {
            params.delete("code");
            const rest = params.toString();
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}${rest ? `?${rest}` : ""}`
            );
        }
    }
    const link = linkRef.current;
    const credentials = useSelector((state: State) => state.auth?.credentials);
    const profile = useSelector((state: State) => state.auth?.profile);

    const handleSignIn = useCallback(
        (session: MagicLinkSession) => {
            dispatch(setToken(session));
        },
        [dispatch]
    );

    const handleProfile = useCallback(
        (userProfile: UserProfile) => {
            dispatch(setProfile(userProfile));
        },
        [dispatch]
    );

    if (credentials && !profile) {
        return (
            <FetchProfileComponent
                accessToken={credentials.accessToken}
                onError={(e) => {
                    console.log(`error fetching profile ${e}`);
                }}
                onProfile={handleProfile}
            />
        );
    }

    if (credentials && profile) {
        return <Redirect to="/" />;
    }

    return <MagicLinkSignIn initialEmail={link.email} initialCode={link.code} onSignIn={handleSignIn} />;
};
